import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

/**
 * Qué tan urgente es un aviso, que es lo único que define quién pasa primero.
 *
 *  - `critico`: la sesión se cayó, mantenimiento. Interrumpe lo que haya.
 *  - `interactivo`: hay una pregunta esperando respuesta (confirmar, OTP, un
 *    input). Intocable: sólo un crítico lo desplaza.
 *  - `resultado`: la consecuencia de algo que el usuario acaba de hacer.
 *  - `aviso`: notificaciones. No interrumpen nunca y se agrupan.
 *  - `oferta`: campañas y ofertas (el usuario Aguacate). La más baja, y la
 *    única que se descarta en vez de esperar turno.
 */
export type Prioridad =
  | 'critico'
  | 'interactivo'
  | 'resultado'
  | 'aviso'
  | 'oferta';

const ORDEN: Record<Prioridad, number> = {
  critico: 0,
  interactivo: 1,
  resultado: 2,
  aviso: 3,
  oferta: 4,
};

interface Pedido {
  prioridad: Prioridad;
  /** Con qué disparar. Conserva la clase original para no perder los mixins. */
  mostrar: () => Promise<SweetAlertResult>;
  resolver: (r: SweetAlertResult) => void;
  /** Para reconocer duplicados. */
  huella: string;
  /** Texto corto, para poder agrupar varios avisos en uno solo. */
  resumen: string;
  /** Ruta donde nació: un aviso de una pantalla que ya no está no se muestra. */
  ruta: string;
  /** Un "Cargando…": nadie lo lee y el código de al lado lo da por pisado. */
  esCargando: boolean;
  /** Se va solo (toast o `timer`): no hace falta que nadie lo cierre. */
  esEfimero: boolean;
}

/** Lo que recibe quien pidió un popup que no llegó a mostrarse. */
const DESCARTADO = {
  isConfirmed: false,
  isDenied: false,
  isDismissed: true,
  dismiss: Swal.DismissReason.close,
} as SweetAlertResult;

/**
 * El portero de los modales.
 *
 * sweetalert2 tiene UN solo popup global. Cuando se abre uno con otro en
 * pantalla, `_main` llama al `_destroy()` del anterior, y ese método nunca
 * resuelve su promesa: el cartel desaparece y —lo grave— el `await Swal.fire`
 * que lo estaba esperando no vuelve nunca. Con él se queda sin ejecutar el
 * `finally` que apagaba el spinner o liberaba el botón, y la pantalla queda
 * muerta hasta que el usuario recarga. Con notificaciones push y campañas
 * corriendo a la vez, eso pasa cada vez más seguido.
 *
 * Acá se centraliza: un solo lugar abre popups y el resto pide turno. Nadie
 * destruye a nadie, y cuando hay que desplazar algo se lo cierra con
 * `Swal.close()`, que sí resuelve la promesa — el llamador recibe "cancelado",
 * su `finally` corre y no queda nada colgado.
 *
 * `instalar()` engancha `Swal.fire` una sola vez, así que los cientos de
 * llamados que ya existen quedan gobernados sin tocar un archivo. El código
 * nuevo puede usar `fire(opts, prioridad)` y declarar su prioridad en vez de
 * dejar que se deduzca.
 */
@Injectable({ providedIn: 'root' })
export class AlertService {
  /** El popup que está en pantalla. */
  private actual: Pedido | null = null;

  private cola: Pedido[] = [];

  private instalado = false;

  /**
   * `Swal.fire` sin el parche. Se guarda al instalar para que el propio
   * portero no se llame a sí mismo al mostrar lo que tiene en cola.
   */
  private disparar: (opts: SweetAlertOptions) => Promise<SweetAlertResult> = (
    opts,
  ) => Swal.fire(opts);

  constructor(private router: Router) {}

  /**
   * Hace que todo `Swal.fire` de la app pase por acá.
   *
   * Se preserva el `this` del llamado: `Swal.mixin({...}).fire()` invoca este
   * mismo `fire` heredado, y si se perdiera la clase se perderían los
   * parámetros del mixin. Los nueve mixins del repo son toasts, así que un
   * toast terminaría dibujado como modal en el centro de la pantalla.
   */
  instalar(): void {
    if (this.instalado) return;
    this.instalado = true;

    const original = Swal.fire;
    const portero = this;

    // `any` en el call: `fire` tiene sobrecargas y TypeScript elige la de
    // tres strings, no la del objeto de opciones.
    this.disparar = (opts: SweetAlertOptions) => (original as any).call(Swal, opts);

    (Swal as any).fire = function (
      this: unknown,
      ...args: any[]
    ): Promise<SweetAlertResult> {
      const clase = this;
      return portero.pedir({
        opts: portero.leerOpts(args),
        esMixin: clase !== Swal,
        mostrar: () => (original as any).apply(clase, args),
      });
    };
  }

  /** Para el código nuevo: el mismo `fire`, pero declarando la prioridad. */
  fire(
    opts: SweetAlertOptions,
    prioridad?: Prioridad,
  ): Promise<SweetAlertResult> {
    return this.pedir({
      opts,
      esMixin: false,
      prioridad,
      mostrar: () => this.disparar(opts),
    });
  }

  // ------------------------------------------------------------------ turnos

  private pedir(entrada: {
    opts: SweetAlertOptions;
    esMixin: boolean;
    prioridad?: Prioridad;
    mostrar: () => Promise<SweetAlertResult>;
  }): Promise<SweetAlertResult> {
    const prioridad =
      entrada.prioridad ?? this.clasificar(entrada.opts, entrada.esMixin);
    const resumen = this.resumir(entrada.opts);
    const huella = `${prioridad}|${resumen}`;

    // El mismo aviso dos veces es uno. Con push y campañas, esta línea es la
    // diferencia entre un cartel y ocho.
    if (
      this.actual?.huella === huella ||
      this.cola.some((p) => p.huella === huella)
    ) {
      return Promise.resolve(DESCARTADO);
    }

    let resolver!: (r: SweetAlertResult) => void;
    const promesa = new Promise<SweetAlertResult>((res) => (resolver = res));

    const pedido: Pedido = {
      prioridad,
      mostrar: entrada.mostrar,
      resolver,
      huella,
      resumen,
      ruta: this.router.url,
      esCargando: this.esCargando(entrada.opts),
      esEfimero: !!entrada.opts?.toast || !!entrada.opts?.timer,
    };

    // `Swal.isVisible()` cubre lo que se haya abierto antes de instalar el
    // portero: de eso no hay registro, pero está en pantalla igual.
    const ocupado = !!this.actual || Swal.isVisible();

    if (!ocupado) {
      this.mostrarAhora(pedido);
      return promesa;
    }

    if (prioridad === 'critico') {
      // Cerrar ANTES de abrir: `close()` resuelve la promesa del anterior, así
      // que el `_destroy` que viene después ya no le roba nada a nadie.
      Swal.close();
      this.mostrarAhora(pedido);
      return promesa;
    }

    // Una oferta no interrumpe a nadie. Si lo que hay en pantalla es un modal,
    // se descarta y se vuelve a ofrecer en la próxima visita: una campaña que
    // aparece diez minutos tarde, encima de otra pantalla, es peor que una que
    // no apareció. Pero si lo que hay es un toast —que se va solo en segundos—
    // espera: perder el panel de bienvenida por un cartelito de paso sería
    // regalar la única oportunidad de mostrarlo.
    if (prioridad === 'oferta') {
      if (!this.actual?.esEfimero) return Promise.resolve(DESCARTADO);
      this.cola.push(pedido);
      return promesa;
    }

    // Un "Cargando…" se reemplaza, no se respeta. Varios flujos del repo
    // abren uno y después disparan el resultado SIN cerrarlo, confiando en
    // que el nuevo pise al viejo (ver `main-rates` al activar un banco, o
    // `detalle-cajero` al guardar un balance). Si esperara turno, ese
    // "Cargando…" se quedaría en pantalla para siempre.
    if (this.actual?.esCargando) {
      Swal.close();
      this.mostrarAhora(pedido);
      return promesa;
    }

    // Un toast en pantalla no puede demorar una respuesta que el usuario está
    // esperando: se lo cierra, se lo devuelve a la cola —no se pierde— y el
    // modal sale ya. Al revés no: un aviso sí espera a que el modal se cierre.
    if (this.actual?.esEfimero && prioridad !== 'aviso') {
      const desplazado = this.actual;
      Swal.close();
      this.cola.push(desplazado);
      this.mostrarAhora(pedido);
      return promesa;
    }

    this.cola.push(pedido);
    return promesa;
  }

  private mostrarAhora(pedido: Pedido): void {
    this.actual = pedido;

    pedido
      .mostrar()
      .then((r) => pedido.resolver(r))
      .catch(() => pedido.resolver(DESCARTADO))
      .finally(() => {
        // Sólo libera el turno si sigue siendo el suyo. Cuando algo lo
        // desplaza, este `finally` corre DESPUÉS de que el nuevo ya se abrió:
        // sin el chequeo borraría el registro del nuevo y sacaría otro de la
        // cola encima, que es justo el pisoteo que este servicio evita.
        if (this.actual !== pedido) return;
        this.actual = null;
        this.siguiente();
      });
  }

  /**
   * Un popup sin botones y sin `timer` no lo puede cerrar el usuario: es un
   * "Cargando…" que sólo cierra el código.
   */
  private esCargando(opts: SweetAlertOptions): boolean {
    return opts?.showConfirmButton === false && !opts?.toast && !opts?.timer;
  }

  private siguiente(): void {
    if (!this.cola.length) return;

    // Lo informativo que perdió su pantalla se descarta. Una confirmación no:
    // alguien está esperando su respuesta para poder seguir.
    this.cola = this.cola.filter((p) => {
      const perdioContexto =
        p.prioridad !== 'critico' &&
        p.prioridad !== 'interactivo' &&
        p.ruta !== this.router.url;
      if (perdioContexto) p.resolver(DESCARTADO);
      return !perdioContexto;
    });

    if (!this.cola.length) return;

    // `sort` es estable, así que dentro de la misma prioridad manda el orden
    // de llegada.
    this.cola.sort((a, b) => ORDEN[a.prioridad] - ORDEN[b.prioridad]);

    if (this.cola[0].prioridad === 'aviso') {
      const avisos = this.cola.filter((p) => p.prioridad === 'aviso');
      this.cola = this.cola.filter((p) => p.prioridad !== 'aviso');
      this.mostrarAhora(
        avisos.length === 1 ? avisos[0] : this.agrupar(avisos),
      );
      return;
    }

    const proximo = this.cola.shift()!;
    this.mostrarAhora(proximo);
  }

  /**
   * Varios avisos esperando se muestran en UNO, con la lista.
   *
   * Tres notificaciones no son tres carteles seguidos: eso es exactamente lo
   * que hace que el usuario cierre sin leer.
   */
  private agrupar(avisos: Pedido[]): Pedido {
    const items = avisos
      .map((a) => `<li style="margin:0 0 .25rem">${this.escapar(a.resumen)}</li>`)
      .join('');

    const opts: SweetAlertOptions = {
      toast: true,
      position: 'top-end',
      icon: 'info',
      title: `${avisos.length} avisos`,
      html:
        '<ul style="margin:.25rem 0 0;padding-left:1.1rem;text-align:left;' +
        'font-size:.85rem;max-height:8rem;overflow-y:auto;' +
        `overflow-wrap:anywhere">${items}</ul>`,
      showConfirmButton: false,
      timer: 6000,
      timerProgressBar: true,
    };

    return {
      prioridad: 'aviso',
      mostrar: () => this.disparar(opts),
      // El resultado del grupo es el resultado de cada uno de los agrupados.
      resolver: (r) => avisos.forEach((a) => a.resolver(r)),
      huella: `aviso|grupo|${avisos.length}`,
      resumen: `${avisos.length} avisos`,
      ruta: this.router.url,
      esCargando: false,
      esEfimero: true,
    };
  }

  // ----------------------------------------------------------- clasificación

  /**
   * Deduce la prioridad de un popup que no la declaró, leyendo sus propios
   * parámetros. Es lo que permite gobernar los llamados que ya existen sin
   * editarlos uno por uno.
   */
  private clasificar(opts: SweetAlertOptions, esMixin: boolean): Prioridad {
    if (esMixin || opts.toast) return 'aviso';
    if (opts.input || opts.showCancelButton || opts.showDenyButton) {
      return 'interactivo';
    }
    if (opts.timer) return 'aviso';
    return 'resultado';
  }

  /** Tolera las dos formas de llamar: un objeto, o `fire(title, text, icon)`. */
  private leerOpts(args: any[]): SweetAlertOptions {
    const primero = args[0];
    if (primero && typeof primero === 'object') {
      return primero as SweetAlertOptions;
    }
    return { title: primero, text: args[1], icon: args[2] } as SweetAlertOptions;
  }

  private resumir(opts: SweetAlertOptions): string {
    const crudo = opts?.title ?? opts?.text ?? opts?.html ?? '';
    return String(typeof crudo === 'string' ? crudo : '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
  }

  /** Los títulos de los avisos los escribe un operador: no van crudos al DOM. */
  private escapar(texto: string): string {
    return texto
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
